import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Sidebar } from "@/components/layout/sidebar";
import { Button } from "@/components/ui/button";
import { ShowcaseCard } from "@/components/showcase-card";
import { ShowcaseItem } from "@/lib/user-types";
import { useAuth } from "@/hooks/use-auth";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function ShowcasePage() {
  const { user } = useAuth();
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  const { data: showcaseItems, isLoading } = useQuery<ShowcaseItem[]>({
    queryKey: ["/api/showcase"],
  });
  
  const styles = [
    { id: "all", name: "All Styles" },
    { id: "cinematic", name: "Cinematic Realism" },
    { id: "wes-anderson", name: "Wes Anderson-esque" },
    { id: "noir", name: "Gritty Noir" },
    { id: "ghibli", name: "Anime Style" },
    { id: "comic", name: "Comic Book" },
    { id: "surreal", name: "Surrealism" },
    { id: "documentary", name: "Documentary" },
    { id: "sitcom", name: "90s Sitcom Style" },
  ];

  const filteredItems = showcaseItems?.filter((item) => {
    const matchesStyle = !selectedStyle || selectedStyle === "all" || item.style.toLowerCase().includes(selectedStyle.toLowerCase());
    const matchesSearch = !searchQuery || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.style.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStyle && matchesSearch;
  });
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {user ? <Sidebar /> : <Header />}
      
      <div className={`flex-1 ${user ? "ml-0 md:ml-64" : ""}`}>
        <main className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Showcase</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Browse through examples of screenplay visualizations created with PreVisly.
                </p>
              </div>
              
              <div className="mt-4 md:mt-0 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search projects..."
                  className="pl-9 w-full md:w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {/* Style filters */}
            <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2">
              {styles.map((style) => (
                <Button
                  key={style.id}
                  variant={selectedStyle === style.id || (style.id === "all" && !selectedStyle) ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedStyle(style.id === "all" ? null : style.id)}
                  className="whitespace-nowrap"
                >
                  {style.name}
                </Button>
              ))}
            </div>
            
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <ShowcaseCardSkeleton key={index} />
                ))}
              </div>
            ) : filteredItems && filteredItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item) => (
                  <ShowcaseCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No showcase items found.</p>
                {searchQuery && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear search
                  </Button>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function ShowcaseCardSkeleton() {
  return (
    <div className="flex flex-col rounded-lg shadow-lg overflow-hidden h-full">
      <div className="flex-shrink-0">
        <Skeleton className="h-48 w-full" />
      </div>
      <div className="flex-1 bg-white p-6 flex flex-col justify-between">
        <div className="flex-1">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-6 w-3/4 mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="mt-6 flex items-center">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="ml-3">
            <Skeleton className="h-4 w-32 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
